import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { documentService } from '@services/document.service';
import { apiErrorStatus } from '@utils/apiErrorMessages';

export interface DocumentDetailModel {
  id: string;
  title: string;
  description: string;
  summary: string;
  category: string;
  categoryName?: string | null;
  tags: string[];
  uploadedBy: string;
  createdBy: string;
  uploadedAt: string;
  fileType: string;
  size: number;
  version: number;
  thumbnail: null;
  status: string;
  permissions: {
    canEdit: boolean;
    canDelete: boolean;
    canShare: boolean;
    canDownload: boolean;
  };
  metadata: Record<string, any>;
  analysisHistory: any[];
  [key: string]: any;
}

interface DocumentDetailState {
  doc: DocumentDetailModel | null;
  loading: boolean;
  error: string | null;
  /** HTTP status of a failed load; null for a network failure or no failure. */
  errorStatus: number | null;
  /** Merges the fields a metadata re-extraction returned into the loaded doc. */
  patchDoc: (patch: Partial<DocumentDetailModel>) => void;
}

export function useDocumentDetail(id: string | undefined): DocumentDetailState {
  const { t } = useTranslation();
  const [doc, setDoc] = useState<DocumentDetailModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);

  useEffect(() => {
    // An answer for an id the hook has moved on from must not land on the new id's state.
    let current = true;
    const fetchDocument = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        setErrorStatus(null);

        const raw = await documentService.getDocument(id);
        if (!current) return;

        setDoc({
          id: raw.id,
          title: raw.title || raw.fileName || '',
          description: raw.description || '',
          summary: raw.summary || '',
          category: raw.category,
          categoryName: raw.categoryName,
          tags: raw.tags || [],
          uploadedBy: raw.createdByName || raw.createdBy || t('common.unknown'),
          createdBy: raw.createdBy || raw.currentVersion?.uploadedBy || '',
          uploadedAt: raw.uploadedAt || raw.createdAt,
          fileType:
            raw.mimeType?.split('/').pop() ||
            raw.currentVersion?.mimeType?.split('/').pop() ||
            'unknown',
          size: raw.fileSize || raw.currentVersion?.fileSize || 0,
          version: raw.version || raw.currentVersion?.versionNumber || 1,
          thumbnail: null,
          status: raw.status || 'active',
          permissions: {
            canEdit: true,
            canDelete: Boolean(raw.canDelete),
            canShare: true,
            canDownload: true,
          },
          metadata: {
            fileName: raw.fileName || raw.currentVersion?.fileName || '',
            mimeType: raw.mimeType || raw.currentVersion?.mimeType || '',
            path: raw.path || '',
            lastModified: raw.updatedAt || raw.createdAt,
          },
          analysisHistory: (raw as any).analysis_history || [],
        } as DocumentDetailModel);
        setLoading(false);
      } catch (err) {
        if (!current) return;
        setDoc(null);
        setErrorStatus(apiErrorStatus(err) ?? null);
        setError(t('documents.failedToLoad'));
        setLoading(false);
      }
    };

    if (id) {
      fetchDocument();
    }
    return () => { current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds error/fallback copy; excluded so a language change doesn't refetch the document/preview (PR #790)
  }, [id]);

  const patchDoc = useCallback((patch: Partial<DocumentDetailModel>) => {
    setDoc((prev) => (prev ? ({ ...prev, ...patch } as DocumentDetailModel) : prev));
  }, []);

  return { doc, loading, error, errorStatus, patchDoc };
}
